/* GCS Kundenstimmen (Startseite) – nur von sections/gcs-reviews.liquid geladen (defer).
   <gcs-reviews-slider>: Scroll-Snap-Track (Wischen bleibt nativ), Buttons vor/zurück auf allen Breiten, Fortschrittsbalken,
   Pfeiltasten links/rechts, solange der Fokus im Slider ist, Ansage „Bewertung 2 von 9“ für Screenreader nach dem Blättern.
   Am Anfang/Ende: aria-disabled statt disabled, damit der Tastaturfokus auf dem Button bleibt.
   „Mehr lesen“: Text auf --rv-lines Zeilen begrenzt (nur wenn er wirklich länger ist). Alle Karten bekommen die Höhe der
   höchsten geschlossenen Karte als Mindesthöhe (--rv-card-min); eine aufgeklappte Karte wächst allein, Nachbarn bleiben stehen.
   Ohne JS: ganzer Text, gleich hohe Karten (Grid), Track per Touch/Trackpad scrollbar, keine Buttons.
   Review-Call 08.10. (A2): auch für die Produktseite (sections/gcs-product-reviews.liquid). Beschriftungen per
   data-label-more / data-label-less am Slider (Standard „Mehr lesen“ / „Weniger anzeigen“). */
(() => {
  if (customElements.get('gcs-reviews-slider')) return;

  const EDGE = 2;
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const LABEL_MORE = 'Mehr lesen';
  const LABEL_LESS = 'Weniger anzeigen';

  class GcsReviewsSlider extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.track = this.querySelector('[data-rv-track]');
      this.slides = this.track ? [...this.track.querySelectorAll('[data-rv-slide]')] : [];
      if (!this.slides.length) return;
      this.ready = true;

      this.controls = this.querySelector('[data-rv-controls]');
      this.prev = this.querySelector('[data-rv-prev]');
      this.next = this.querySelector('[data-rv-next]');
      this.bar = this.querySelector('[data-rv-bar]');
      this.thumb = this.querySelector('[data-rv-thumb]');
      this.status = this.querySelector('[data-rv-status]');
      this.labelMore = this.dataset.labelMore || LABEL_MORE;
      this.labelLess = this.dataset.labelLess || LABEL_LESS;
      this.cards = this.slides.map((slide) => this.setupCard(slide));
      this.classList.add('is-ready');

      this.frame = 0;
      this.width = 0;
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
      if (this.next) this.next.addEventListener('click', () => this.go(1));
      this.addEventListener('keydown', (e) => this.onKey(e));
      // Theme-Editor: ausgewählten Block in den sichtbaren Bereich holen
      this.addEventListener('shopify:block:select', (e) => this.reveal(e.target));

      if ('ResizeObserver' in window) {
        // Nur auf Breitenänderungen reagieren (Aufklappen ändert die Höhe und soll nichts neu messen)
        this.ro = new ResizeObserver(() => {
          const w = this.track.clientWidth;
          if (w === this.width) return;
          this.width = w;
          this.layout();
        });
        this.ro.observe(this.track);
      }
      this.width = this.track.clientWidth;
      this.layout();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.layout());
    }

    disconnectedCallback() {
      if (this.track && this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
      if (this.ro) this.ro.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      clearTimeout(this.announceTimer);
    }

    setupCard(slide) {
      const item = {
        card: slide.querySelector('[data-rv-card]') || slide,
        text: slide.querySelector('[data-rv-text]'),
        btn: slide.querySelector('[data-rv-more]'),
        expanded: false,
      };
      if (item.text && item.btn) item.btn.addEventListener('click', () => this.toggle(item));
      return item;
    }

    /* Zeilenbegrenzung: nur kürzen, wenn der Text wirklich über die Zeilenzahl hinausgeht. */
    measure(item) {
      if (!item.text || !item.btn || item.expanded) return;
      item.text.classList.add('is-clamped');
      const overflows = item.text.scrollHeight - item.text.clientHeight > 1;
      if (!overflows) item.text.classList.remove('is-clamped');
      item.btn.hidden = !overflows;
    }

    equalize() {
      this.track.style.setProperty('--rv-card-min', '0px');
      let max = 0;
      this.cards.forEach((item) => {
        if (!item.expanded) max = Math.max(max, item.card.offsetHeight);
      });
      this.track.style.setProperty('--rv-card-min', `${max}px`);
    }

    layout() {
      this.cards.forEach((item) => this.measure(item));
      this.equalize();
      this.update();
    }

    toggle(item) {
      item.expanded = !item.expanded;
      item.text.classList.toggle('is-clamped', !item.expanded);
      item.card.classList.toggle('is-expanded', item.expanded);
      item.btn.setAttribute('aria-expanded', String(item.expanded));
      item.btn.textContent = item.expanded ? this.labelLess : this.labelMore;
      if (!item.expanded) this.equalize();
      this.update();
    }

    offsets() {
      const base = this.slides[0].offsetLeft;
      return this.slides.map((slide) => slide.offsetLeft - base);
    }

    maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    /* Index der Karte, die am linken Rand steht */
    index() {
      const x = this.track.scrollLeft;
      let best = 0;
      this.offsets().forEach((o, i, all) => {
        if (Math.abs(o - x) < Math.abs(all[best] - x)) best = i;
      });
      return best;
    }

    /* Ganz sichtbare Karten (mobil 1, Tablet 2, Desktop 3 bzw. 4) = Schrittweite der Buttons */
    perView() {
      const offs = this.offsets();
      const width = this.slides[0].getBoundingClientRect().width;
      const pitch = offs[1] || width;
      const gap = Math.max(0, pitch - width);
      const view = this.track.clientWidth - (parseFloat(getComputedStyle(this.track).paddingLeft) || 0);
      return Math.max(1, Math.floor((view + gap + 1) / pitch));
    }

    scrollToIndex(i) {
      const offs = this.offsets();
      const target = Math.min(Math.max(i, 0), offs.length - 1);
      this.track.scrollTo({ left: Math.min(offs[target], this.maxScroll()), behavior: reduceMotion() ? 'auto' : 'smooth' });
    }

    go(dir) {
      const btn = dir < 0 ? this.prev : this.next;
      if (btn && btn.getAttribute('aria-disabled') === 'true') return;
      const max = this.maxScroll();
      const x = this.track.scrollLeft;
      if ((dir < 0 && x <= EDGE) || (dir > 0 && x >= max - EDGE)) return;
      this.scrollToIndex(this.index() + dir * this.perView());
      this.announceLater();
    }

    onKey(e) {
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      e.preventDefault();
      this.go(e.key === 'ArrowLeft' ? -1 : 1);
    }

    reveal(target) {
      const slide = target && target.closest ? target.closest('[data-rv-slide]') : null;
      const i = this.slides.indexOf(slide);
      if (i >= 0) this.scrollToIndex(i);
    }

    setDisabled(btn, off) {
      if (btn) btn.setAttribute('aria-disabled', String(off));
    }

    update() {
      const max = this.maxScroll();
      const x = this.track.scrollLeft;
      const scrollable = max > EDGE;
      if (this.controls) this.controls.hidden = !scrollable;
      this.setDisabled(this.prev, !scrollable || x <= EDGE);
      this.setDisabled(this.next, !scrollable || x >= max - EDGE);
      if (scrollable && this.bar && this.thumb) {
        const barW = this.bar.clientWidth;
        const ratio = Math.min(1, this.track.clientWidth / this.track.scrollWidth);
        const thumbW = Math.max(24, Math.round(barW * ratio));
        const pos = max > 0 ? Math.min(1, Math.max(0, x / max)) : 0;
        this.thumb.style.width = `${thumbW}px`;
        this.thumb.style.transform = `translateX(${Math.round((barW - thumbW) * pos)}px)`;
      }
    }

    announceLater() {
      if (!this.status) return;
      clearTimeout(this.announceTimer);
      const run = () => {
        clearTimeout(this.announceTimer);
        this.track.removeEventListener('scrollend', run);
        this.announce();
      };
      this.track.addEventListener('scrollend', run, { once: true });
      this.announceTimer = setTimeout(run, reduceMotion() ? 60 : 900);
    }

    announce() {
      const n = this.slides.length;
      const first = this.index() + 1;
      const last = Math.min(n, first + this.perView() - 1);
      this.status.textContent =
        last > first ? `Bewertungen ${first} bis ${last} von ${n}` : `Bewertung ${first} von ${n}`;
    }
  }

  customElements.define('gcs-reviews-slider', GcsReviewsSlider);
})();
